import { cloneDeep } from 'lodash/fp'
import { SUBSCRIPTION_STATE } from '../constants'
import { platforms } from '../../constants'

/*
 * Redux-saga selector functions.
 * Used with the `select` effect in sagas to Retrieves
 *      specific portions of the state.
 */

/**
 * Retrieves the config options provided by the auth plugin.
 * @method getAuthConfig
 * @return {Object}
 */
export function getAuthConfig (state) {
  return cloneDeep(state.config.authentication)
}

/**
 * Retrieves the subscription information of a platform.
 * @method getSubscriptionInfo
 * @param  {string} [platform] - The backend platform in use.
 * @return {Object}
 */
export function getSubscriptionInfo (state, platform) {
  if (!platform) {
    platform = getPlatform(state)
  }

  if (state.authentication.subscription) {
    return cloneDeep(state.authentication.subscription[platform])
  } else {
    return undefined
  }
}

/**
 * Retrieves the connection information of a platform.
 * Includes the info needed to make requests to that platform.
 * @method getConnectionInfo
 * @param  {string} [platform] - The backend platform in use.
 * @return {Object}
 */
export function getConnectionInfo (state, platform) {
  if (!platform) {
    platform = getPlatform(state)
  }

  if (state.authentication.connection) {
    return cloneDeep(state.authentication.connection[platform])
  } else {
    return undefined
  }
}

/**
 * Retrieves the domain of the callee address information of a platform.
 * @method getDomain
 * @return {string}
 */
export function getDomain (state) {
  const { username = '', identity = username } = getUserInfo(state)
  const [, domain = ''] = identity.split('@')
  return domain
}

/**
 * Retrieves the identity of the currently logged-in user.
 * The identity is of the form: <userName>@<domain>
 * @method getIdentity
 * @return {string}
 */
export function getIdentity (state) {
  const userInfo = getUserInfo(state)
  return userInfo.identity || userInfo.username || ''
}

/**
 * Retrieves the user information.
 * @method getUserInfo
 * @return {Object} An object whose properties are: accessToken, identity & username. Identity is user's primary contact address.
 */
export function getUserInfo (state) {
  return cloneDeep(state.authentication.userInfo) || {}
}

/**
 * Retrieves a flag from the store representing if the login scenario was SSO or not.
 * @method getAuthScenario
 * @return {boolean}
 */
export function getAuthScenario (state) {
  return cloneDeep(state.authentication.isSSO)
}

/**
 * Retrieves information about the services that the user is subscribed for.
 * @method getServices
 * @return {Object}
 */
export function getServices (state, platform) {
  const subscription = getSubscriptionInfo(state, platform)

  if (subscription && subscription.servicesInfo) {
    return subscription.servicesInfo
  } else {
    return {
      // TODO: Have default as part of the reducer.
      requested: [],
      received: [],
      missing: [],
      status: SUBSCRIPTION_STATE.NONE,
      services: {}
    }
  }
}

/**
 * Retrieves the name of the backend platform the user is currently subscribed to.
 * @method getPlatform
 * @return {string}
 */
export function getPlatform (state) {
  let platform = state.authentication.platform

  /**
   * Link and UC set the platform in auth state after setting credentials /
   *    connecting, but CPaaS does not (because the idea was to move away from
   *    needing the platform). So if there isn't a platform, but there is a
   *    CPaaS-specific identity, then assume the platform is CPaaS.
   * This was needed for the request plugin to get the right platform when using
   *    the `getRequestInfo` selector below.
   * This is a workaround for bad design.
   */
  if (!platform && getUserInfo(state).identity) {
    platform = 'cpaas'
  }

  return platform
}

/**
 * Retrieves info needed to make a REST request for the different platforms.
 * @method getRequestInfo
 * @return {Object}
 */
export function getRequestInfo (state, platform) {
  if (!platform) {
    platform = getPlatform(state)
  }

  const { subscription, clientCorrelator } = getAuthConfig(state)
  let { server } = getAuthConfig(state)
  let { userInfo } = cloneDeep(state.authentication)

  // For backwards compatibility, we need to fall back to
  // subscription for the server connection details
  if (!server) {
    server = {
      protocol: subscription.protocol,
      base: subscription.server,
      port: subscription.port,
      version: subscription.version
    }
  }

  // If user info does not exist yet, use an empty object. Requests will still fail, but they will be handled.
  if (!userInfo) {
    userInfo = {}
  }

  let requestInfo

  if (platform === platforms.CPAAS) {
    requestInfo = {
      baseURL: `${server.protocol}://${server.base}:${server.port}`,
      version: server.version,
      username: userInfo.username,
      // TODO: Token doesn't need to be here if its in requestOptions.
      token: userInfo.accessToken,
      clientCorrelator,
      // TODO: DO NOT hardcode this here (?). The original idea was that this
      //    is set in state after connection.
      options: {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${userInfo.accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    }
  } else if (platform === platforms.UC) {
    requestInfo = {
      baseURL: `${subscription.protocol}://${subscription.server}:${subscription.port}`,
      version: subscription.version,
      username: userInfo.username
    }
  } else if (platform === platforms.LINK) {
    requestInfo = {
      baseURL: `${server.protocol}://${server.base}:${server.port}`,
      version: server.version,
      username: userInfo.username
    }

    /*
     * If the requested platform was Link but the platform set in state is
     *    UC, and so we're using UC but making a request for SPiDR.
     * Change the requestInfo provided to ensure the URL will be valid for
     *    SPiDR and authentication will be valid for CIM.
     */
    const setPlatform = getPlatform(state)
    if (setPlatform === platforms.UC) {
      requestInfo.version = '1'

      const connInfo = getConnectionInfo(state, setPlatform)
      if (connInfo && connInfo.requestOptions) {
        requestInfo.requestOptions = connInfo.requestOptions
      }
      return requestInfo
    }
  } else {
    // Platform is not supported
    return {}
  }

  const connInfo = getConnectionInfo(state, platform)
  if (connInfo && connInfo.requestOptions) {
    requestInfo.requestOptions = connInfo.requestOptions
  }

  return requestInfo
}
